import React, { useState, useEffect } from 'react'
import Modal from '../../../../ui_reusable/Modal'
import { StandardInput } from '../../../../ui_reusable/StandardInputs'
import {
  FormGroup,
  Label,
  ErrorMessage,
} from '../../../../ui_reusable/styles/CreateInputUtils.styles'
import { RequiredLabelStar } from './styles/DatasetForm.styles'

interface RegionCreateModalProps {
  open: boolean
  onClose: () => void
  onConfirm: (name: string, population: number) => void
  initialName: string
}

const RegionCreateModal: React.FC<RegionCreateModalProps> = ({
  open,
  onClose,
  onConfirm,
  initialName,
}) => {
  const [name, setName] = useState(initialName)
  const [population, setPopulation] = useState<string>('')
  const [errors, setErrors] = useState<{
    name?: string
    population?: string
  }>({})

  // Reset form when modal opens/closes or initial name changes
  useEffect(() => {
    if (open) {
      setName(initialName)
      setPopulation('')
      setErrors({})
    }
  }, [open, initialName])

  const validate = (): boolean => {
    const newErrors: { name?: string; population?: string } = {}

    if (!name.trim()) {
      newErrors.name = 'Region name is required'
    }

    if (!population.trim()) {
      newErrors.population = 'Population is required'
    } else {
      const populationNum = parseInt(population, 10)
      if (isNaN(populationNum)) {
        newErrors.population = 'Population must be a valid number'
      } else if (populationNum < 0) {
        newErrors.population = 'Population must be a positive number'
      } else if (populationNum > 2147483647) {
        newErrors.population = 'Population is too large'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleConfirm = () => {
    if (validate()) {
      const populationNum = parseInt(population, 10)
      onConfirm(name.trim(), populationNum)
      onClose()
    }
  }

  const handleCancel = () => {
    setErrors({})
    onClose()
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleConfirm()
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      title="Create New Region"
      size="sm"
    >
      <Modal.Body>
        <div style={{ padding: '1rem', minWidth: '400px' }}>
          <FormGroup>
            <Label>
              Region Name
              <RequiredLabelStar>*</RequiredLabelStar>
            </Label>
            <StandardInput
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyUp={handleKeyPress}
              placeholder="Enter region name"
              autoFocus
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </FormGroup>

          <FormGroup style={{ marginTop: '8px' }}>
            <Label>
              Population
              <RequiredLabelStar>*</RequiredLabelStar>
            </Label>
            <StandardInput
              type="number"
              value={population}
              onChange={(e) => setPopulation(e.target.value)}
              onKeyUp={handleKeyPress}
              placeholder="Enter population"
              min="0"
            />
            {errors.population && (
              <ErrorMessage>{errors.population}</ErrorMessage>
            )}
          </FormGroup>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Modal.DefaultFooter
          confirmText="Create Region"
          cancelText="Cancel"
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      </Modal.Footer>
    </Modal>
  )
}

export default RegionCreateModal
